<main [ngClass]="{ 'dark': dark$ | async, cardInfoStyle }"id="site__main" [class.reputation-page-shell]="isWidget">
    <div [ngStyle]="darkTheme" class="reputation-page-shell__inner margin-top-6">
        <div class="reputation-page-shell__heading">
            <h1 class="reputation-page-shell__title">{{ 'REPUTATIONS' | translate }}</h1>
        </div>

        <div class="reputation-content" *ngIf="!loading; else reputationLoading">
            <section class="status-hero" [ngStyle]="{ '--status-color': statusAccentColor }" [style]="cardStyle">
                <div class="status-hero__top">
                    <div class="status-hero__copy">
                        <p class="status-hero__eyebrow">{{ 'REPUTATION_STATUS_TITLE' | translate }}</p>
                        <div class="status-hero__headline">
                            <span class="status-hero__title">{{ statusHero.title }}</span>
                            <span class="status-hero__chip" *ngIf="statusHero.levelLabel">{{ statusHero.levelLabel
                                }}</span>
                        </div>
                        <p class="status-hero__text" *ngIf="!isFinalLevel; else finalStatusMessage">
                            {{ 'REPUTATION_STATUS_SUBTITLE' | translate : { level: statusHero.nextTitle } }}
                        </p>
                        <ng-template #finalStatusMessage>
                            <p class="status-hero__text">{{ 'REPUTATION_STATUS_FINAL' | translate }}</p>
                        </ng-template>
                    </div>
                    <div class="status-hero__badge" aria-hidden="true">
                        <div class="status-hero__badge-inner">
                            <img *ngIf="statusHero.iconUrl; else statusBadgeFallback" [src]="statusHero.iconUrl"
                                [alt]="statusHero.title">
                            <ng-template #statusBadgeFallback>
                                <span class="status-hero__badge-fallback">{{ statusHero.title | slice: 0:1 }}</span>
                            </ng-template>
                        </div>
                    </div>
                </div>

                <div class="status-hero__panel" [style]="cardStyle">
                    <div class="status-hero__metric">
                        <p class="status-hero__metric-label">{{ 'REPUTATION_POINTS_LABEL' | translate }}</p>
                        <p class="status-hero__points">
                            <span>{{ statusHero.currentPointsLabel }}</span>
                            <span class="status-hero__points-divider">/</span>
                            <span class="d-flex gap-1">
                                <span class="status-hero__points-goal">{{ statusHero.targetPointsLabel }}</span>
                                <img class="loyalty-icon status-hero__points-icon" [src]="loyaltyIconSrc" alt="Points" />
                            </span>
                        </p>

                        <div class="status-hero__progress-row">
                            <div class="status-hero__progress">
                                <div class="status-hero__progress-fill" [style.width.%]="statusHero.progressValue"></div>
                            </div>
                            <span class="status-hero__progress-value">{{ statusHero.progressValue | number : '1.0-0' }}%</span>
                        </div>
                    </div>

                    <div class="status-hero__divider"></div>

                    <div class="status-hero__metric">
                        <p class="status-hero__metric-label">{{ 'NEXT_LEVEL' | translate }}</p>
                        <p class="status-hero__next" *ngIf="!isFinalLevel; else heroFinalState">
                            <strong>{{ statusHero.remainingPointsLabel }}</strong>
                            <img class="loyalty-icon status-hero__points-icon" [src]="loyaltyIconSrc" alt="Points" />
                            <span>{{ 'REPUTATION_UNLOCK_LEVEL' | translate : { level: statusHero.nextTitle } }}</span>
                        </p>
                        <ng-template #heroFinalState>
                            <p class="status-hero__next status-hero__next--final">{{ 'REPUTATION_STATUS_FINAL' |
                                translate }}</p>
                        </ng-template>
                    </div>
                </div>
            </section>

            <ng-container *ngIf="activeLevelInactivityWarning as inactivity">
                <section class="loss-banner" *ngIf="countdownVisible && countdownBlocks as timer">
                    <div class="loss-banner__icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path fill-rule="evenodd"
                                d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5Zm.75 4.5a.75.75 0 0 0-1.5 0v5.69c0 .2.08.39.22.53l3 3a.75.75 0 1 0 1.06-1.06l-2.78-2.78V6.75Z"
                                clip-rule="evenodd" />
                        </svg>
                    </div>

                    <div class="loss-banner__copy">
                        <p class="loss-banner__title">{{ 'AVOID_LOSING' | translate }}</p>
                        <p class="loss-banner__text">
                            {{ 'ACTIVITY_BEFORE_COUNTDOWN' | translate }}
                            <strong>{{ formatPoints(inactivity.lossAmount) }}</strong>
                            <img class="loyalty-icon" [src]="loyaltyIconSrc" alt="Points" />
                            {{ 'FROM_WALLET' | translate }}
                        </p>
                    </div>

                    <div class="loss-banner__timer">
                        <div class="loss-banner__time">
                            <span class="loss-banner__time-value">{{ timer.days | number : '2.0-0' }}</span>
                            <small class="loss-banner__time-label">{{ 'DAYS' | translate }}</small>
                        </div>
                        <div class="loss-banner__time">
                            <span class="loss-banner__time-value">{{ timer.hours | number : '2.0-0' }}</span>
                            <small class="loss-banner__time-label">{{ 'HOURS' | translate }}</small>
                        </div>
                        <div class="loss-banner__time">
                            <span class="loss-banner__time-value">{{ timer.minutes | number : '2.0-0' }}</span>
                            <small class="loss-banner__time-label">{{ 'MINUTES' | translate }}</small>
                        </div>
                        <div class="loss-banner__time">
                            <span class="loss-banner__time-value">{{ timer.seconds | number : '2.0-0' }}</span>
                            <small class="loss-banner__time-label">{{ 'SECONDS' | translate }}</small>
                        </div>
                    </div>
                </section>
            </ng-container>

            <section id="reputation-levels" class="levels-overview" [style]="cardStyle">
                <div class="levels-overview__header">
                    <div>
                        <p class="levels-overview__eyebrow">{{ 'REPUTATIONS' | translate }}</p>
                        <h3 class="levels-overview__title">{{ 'CHECK_UNLOCKED_LOCKED_BADGES' | translate }}</h3>
                    </div>
                </div>

                <div class="levels-overview__scroll">
                    <div class="levels-overview__track"
                        [class.levels-overview__track--single]="(!currentLevel && !reputationRailLevels.length) || (currentLevel && reputationRailLevels.length === 1)">
                        <article *ngIf="!currentLevel && preLevel" class="level-node level-node--current"
                            [class.level-node--selected]="isPreLevelBenefitSelected"
                            [style.--level-color]="statusAccentColor" (click)="selectBenefitLevel(null)"
                            (keydown)="handleBenefitLevelKeydown($event, null)" tabindex="0" role="button"
                            [attr.aria-pressed]="isPreLevelBenefitSelected">
                            <span class="level-node__badge">{{ 'YOU_ARE_HERE' | translate }}</span>
                            <div class="level-node__shell">
                                <div class="level-node__icon-wrap">
                                    <div class="level-node__icon" [ngStyle]="preLevelBadgeStyles">
                                        <img *ngIf="preLevelPictureUrl" [src]="preLevelPictureUrl"
                                            [alt]="preLevelTitle" />
                                        <span *ngIf="!preLevelPictureUrl">{{ preLevelTitle | slice: 0:1 }}</span>
                                    </div>
                                </div>

                                <p class="level-node__title">{{ preLevelTitle }}</p>
                                <p class="level-node__meta">
                                    <strong>{{ statusHero.currentPointsLabel }}</strong>
                                    <img class="loyalty-icon" [src]="loyaltyIconSrc" alt="Points" />
                                </p>
                            </div>

                            <div class="level-note level-note--warning" *ngIf="railWarning.show">
                                <strong>
                                    {{ 'YOU_LOSE' | translate }}
                                    <span class="level-note__metric">
                                        {{ railWarning.lossLabel }}
                                        <img class="loyalty-icon" [src]="loyaltyIconSrc" alt="Points" />
                                    </span>
                                </strong>
                                <span>{{ 'IF_INACTIVE' | translate }} {{ railWarning.durationText }}.</span>
                            </div>
                        </article>

                        <div *ngIf="!currentLevel && preLevel && reputationRailLevels.length"
                            class="level-connector level-connector--locked"
                            [style.--connector-color]="statusAccentColor">
                            <span class="level-connector__line"></span>
                            <span class="level-connector__icon" aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                    <path fill-rule="evenodd"
                                        d="M12 1.5a4.5 4.5 0 0 0-4.5 4.5V9A2.25 2.25 0 0 0 5.25 11.25v7.5A2.25 2.25 0 0 0 7.5 21h9a2.25 2.25 0 0 0 2.25-2.25v-7.5A2.25 2.25 0 0 0 16.5 9V6A4.5 4.5 0 0 0 12 1.5Zm3 7.5V6a3 3 0 1 0-6 0V9h6Z"
                                        clip-rule="evenodd" />
                                </svg>
                            </span>
                        </div>

                        <ng-container *ngFor="let level of reputationRailLevels; trackBy: trackByLevel; let index = index">
                            <article class="level-node" [class.level-node--current]="level.railState === 'current'"
                                [class.level-node--previous]="level.railState === 'previous'"
                                [class.level-node--locked]="level.railState === 'locked'"
                                [class.level-node--selected]="level.isBenefitSelected"
                                [style.--level-color]="level.resolvedColor">
                                <span class="level-node__badge" *ngIf="level.railState === 'current'">{{ 'YOU_ARE_HERE'
                                    | translate }}</span>

                                <div class="level-node__shell" (click)="selectBenefitLevel(level)"
                                    (keydown)="handleBenefitLevelKeydown($event, level)" tabindex="0" role="button"
                                    [attr.aria-pressed]="level.isBenefitSelected">
                                    <div class="level-node__icon-wrap">
                                        <div class="level-node__icon"
                                            [style.background]="level.railState === 'locked' ? null : level.resolvedColor">
                                            <img *ngIf="level.pictureUrl" [src]="level.pictureUrl"
                                                [alt]="level.title" />
                                            <span *ngIf="!level.pictureUrl">{{ level.title | slice: 0:1 }}</span>
                                        </div>

                                        <span class="level-node__lock" *ngIf="level.railShowLock" aria-hidden="true">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                fill="currentColor">
                                                <path fill-rule="evenodd"
                                                    d="M12 1.5a4.5 4.5 0 0 0-4.5 4.5V9A2.25 2.25 0 0 0 5.25 11.25v7.5A2.25 2.25 0 0 0 7.5 21h9a2.25 2.25 0 0 0 2.25-2.25v-7.5A2.25 2.25 0 0 0 16.5 9V6A4.5 4.5 0 0 0 12 1.5Zm3 7.5V6a3 3 0 1 0-6 0V9h6Z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </span>
                                    </div>

                                    <p class="level-node__title">{{ level.title }}</p>
                                    <p class="level-node__meta">
                                        <strong>{{ level.railMetricText }}</strong>
                                        <img *ngIf="level.railShowsPointsIcon" class="loyalty-icon"
                                            [src]="loyaltyIconSrc" alt="Points" />
                                    </p>
                                </div>

                                <div class="level-note level-note--warning"
                                    *ngIf="railWarning.show && index === 0 && !level.railNoteValueText">
                                    <strong>
                                        {{ 'YOU_LOSE' | translate }}
                                        <span class="level-note__metric">
                                            {{ railWarning.lossLabel }}
                                            <img class="loyalty-icon" [src]="loyaltyIconSrc" alt="Points" />
                                        </span>
                                    </strong>
                                    <span>{{ 'IF_INACTIVE' | translate }} {{ railWarning.durationText }}.</span>
                                </div>

                                <div class="level-note level-note--unlock" *ngIf="level.railNoteValueText">
                                    <strong>
                                        {{ level.railNoteValueText }}
                                        <img class="loyalty-icon" [src]="loyaltyIconSrc" alt="Points" />
                                    </strong>
                                    <span>{{ 'REPUTATION_UNLOCK_LEVEL' | translate : { level: level.title } }}</span>
                                </div>
                            </article>

                            <div *ngIf="!level.isLast" class="level-connector"
                                [class.level-connector--locked]="level.nextRailState === 'locked'"
                                [class.level-connector--passed]="level.nextRailState !== 'locked'"
                                [style.--connector-color]="level.resolvedColor">
                                <span class="level-connector__line"></span>
                                <span class="level-connector__icon" aria-hidden="true">
                                    <svg *ngIf="level.nextRailState === 'locked'"
                                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                        <path fill-rule="evenodd"
                                            d="M12 1.5a4.5 4.5 0 0 0-4.5 4.5V9A2.25 2.25 0 0 0 5.25 11.25v7.5A2.25 2.25 0 0 0 7.5 21h9a2.25 2.25 0 0 0 2.25-2.25v-7.5A2.25 2.25 0 0 0 16.5 9V6A4.5 4.5 0 0 0 12 1.5Zm3 7.5V6a3 3 0 1 0-6 0V9h6Z"
                                            clip-rule="evenodd" />
                                    </svg>
                                    <svg *ngIf="level.nextRailState !== 'locked'"
                                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                        <path fill-rule="evenodd"
                                            d="M6.219 6.22a.75.75 0 0 1 1.06 0L12 10.94l4.72-4.72a.75.75 0 1 1 1.06 1.06L13.06 12l4.72 4.72a.75.75 0 1 1-1.06 1.06L12 13.06l-4.72 4.72a.75.75 0 1 1-1.06-1.06L10.94 12 6.22 7.28a.75.75 0 0 1 0-1.06Z"
                                            clip-rule="evenodd" />
                                    </svg>
                                </span>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </section>

            <section class="benefits-card" [style]="cardStyle" *ngIf="selectedBenefits.showCard"
                [ngStyle]="{ '--benefit-color': selectedBenefits.color }">
                <div class="benefits-card__header">
                    <div class="benefits-card__header-main">
                        <span class="benefits-card__header-icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M11.25 2.25a2.25 2.25 0 0 0-2.25 2.25c0 .414.112.801.307 1.132A2.998 2.998 0 0 0 6 8.625V9H5.25A2.25 2.25 0 0 0 3 11.25v1.5A2.25 2.25 0 0 0 5.25 15H6v3.75A2.25 2.25 0 0 0 8.25 21h7.5A2.25 2.25 0 0 0 18 18.75V15h.75A2.25 2.25 0 0 0 21 12.75v-1.5A2.25 2.25 0 0 0 18.75 9H18v-.375a2.998 2.998 0 0 0-3.307-2.993 2.24 2.24 0 0 0 .307-1.132 2.25 2.25 0 0 0-3.75-1.688A2.243 2.243 0 0 0 11.25 2.25Zm-.75 6V4.5a.75.75 0 1 1 1.5 0v3.75h-1.5Zm-3 2.25A1.5 1.5 0 0 1 9 9h1.5v4.5h-3a.75.75 0 0 1-.75-.75v-1.5c0-.414.336-.75.75-.75Zm4.5 3V9H15a1.5 1.5 0 0 1 1.5 1.5c.414 0 .75.336.75.75v1.5a.75.75 0 0 1-.75.75h-4.5ZM7.5 15h3v4.5h-2.25a.75.75 0 0 1-.75-.75V15Zm4.5 4.5V15h3v3.75a.75.75 0 0 1-.75.75H12Z" />
                            </svg>
                        </span>
                        <div>
                            <p class="benefits-card__eyebrow">{{ 'BENEFITS' | translate }}</p>
                            <h3 class="benefits-card__title">{{ 'BENEFITS' | translate }} <span
                                    class="benefits-card__title-accent">({{ selectedBenefits.title }})</span></h3>
                        </div>
                    </div>
                    <button *ngIf="selectedBenefits.description" type="button" class="benefits-card__link" (click)="openBenefitsPopup()"
                        [attr.aria-expanded]="benefitsPopupOpen" aria-haspopup="dialog">
                        {{ 'SEE_DESCRIPTIONS' | translate }}
                    </button>
                </div>

                <div class="benefits-card__items" *ngIf="selectedBenefits.hasItems">
                    <article class="benefit-item" *ngFor="let benefit of selectedBenefits.items">
                        <span class="benefit-item__icon" aria-hidden="true">{{ benefit.icon }}</span>

                        <div class="benefit-item__copy">
                            <p class="benefit-item__title">{{ benefit.title }}</p>
                            <p class="benefit-item__text" *ngIf="benefit.description">{{ benefit.description }}</p>
                        </div>
                    </article>
                </div>
            </section>

            <section>
                <div [style]="cardStyle" class="benefits-popup__tip">
                    <span class="benefits-popup__tip-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M11.48 3.499a.75.75 0 0 1 1.04 0l2.132 2.091 2.948.428a.75.75 0 0 1 .416 1.279l-2.133 2.08.504 2.936a.75.75 0 0 1-1.088.79L12 11.982l-2.638 1.387a.75.75 0 0 1-1.088-.79l.504-2.936-2.133-2.08a.75.75 0 0 1 .416-1.28l2.948-.427L11.48 3.5Z" />
                            <path
                                d="M6.25 15.75a.75.75 0 0 1 .75.75v1.69l1.22-.64a.75.75 0 0 1 .697 1.328l-2.325 1.22A.75.75 0 0 1 5.5 19.437V16.5a.75.75 0 0 1 .75-.75Zm11.5 0a.75.75 0 0 1 .75.75v2.937a.75.75 0 0 1-1.092.663l-2.325-1.22a.75.75 0 1 1 .697-1.328l1.22.64V16.5a.75.75 0 0 1 .75-.75Z" />
                        </svg>
                    </span>

                    <div class="benefits-popup__tip-copy">
                        <p class="benefits-popup__tip-title">{{ 'CONSEIL' | translate }}</p>
                        <p class="benefits-popup__tip-text">{{ 'BENEFITS_ADVICE_TEXT' | translate }}</p>
                    </div>

                    <span class="benefits-popup__tip-graphic" aria-hidden="true">
                        <svg viewBox="0 0 160 84" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 64L40 36L66 54L95 22L122 34L152 6" stroke="currentColor" stroke-width="10"
                                stroke-linecap="round" stroke-linejoin="round" opacity="0.38" />
                            <path d="M126 10L152 6L146 31" stroke="currentColor" stroke-width="10"
                                stroke-linecap="round" stroke-linejoin="round" opacity="0.38" />
                        </svg>
                    </span>
                </div>
            </section>
        </div>

        <ng-template #reputationLoading>
            <div class="reputation-content">
                <div class="skeleton skeleton--card"></div>
                <div class="skeleton skeleton--card"></div>
                <div class="skeleton skeleton--card"></div>
            </div>
        </ng-template>

        <app-bottom-sheet [height]="'auto'" [open]="benefitsPopupOpen" (close)="closeBenefitsPopup()"
            [ariaLabel]="'BENEFITS' | translate" [darkTheme]="darkTheme" [dark]="!!(dark$ | async)">
            <div sheetHeader class="benefits-sheet__header">
                <div>
                    <h2 class="benefits-sheet__title">{{ 'BENEFITS' | translate }}</h2>
                    <p class="benefits-sheet__subtitle">{{ selectedBenefits.title }}</p>
                </div>
                <button type="button" class="benefits-sheet__close" (click)="closeBenefitsPopup()" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fill-rule="evenodd"
                            d="M6.219 6.22a.75.75 0 0 1 1.06 0L12 10.94l4.72-4.72a.75.75 0 1 1 1.06 1.06L13.06 12l4.72 4.72a.75.75 0 1 1-1.06 1.06L12 13.06l-4.72 4.72a.75.75 0 1 1-1.06-1.06L10.94 12 6.22 7.28a.75.75 0 0 1 0-1.06Z"
                            clip-rule="evenodd" />
                    </svg>
                </button>
            </div>

            <div sheetBody>
                <div class="benefits-sheet__description-shell">
                    <quill-view-html class="benefits-sheet__description" [content]="selectedBenefits.description"
                        theme="snow">
                    </quill-view-html>
                </div>
            </div>
        </app-bottom-sheet>
    </div>
</main>